<!--
  MarkdownField — markdown renderer; the chrome-free inner GridCell hosts.
  `modelValue` is the raw markdown string. Inline: truncated text. Active: a
  read-only MarkdownEditor, un-truncated. Editing: the shared MarkdownEditModal
  opens — save emits the new value + commit, cancel emits cancel.
-->
<template>
  <div v-if="mode === 'inline'" class="markdown-field" :class="sizeClass">
    <span class="cell-value">{{ displayValue }}</span>
  </div>
  <template v-else>
    <div class="markdown-field-preview">
      <MarkdownEditor :model-value="displayValue" :editable="false" />
      <button v-if="editable" class="btn expand-record" title="Edit">
        <font-awesome-icon icon="up-right-and-down-left-from-center" size="xs" />
      </button>
    </div>
    <MarkdownEditModal
      ref="modal"
      :model-value="displayValue"
      @update:model-value="handleSave"
      @cancel="emit('cancel')"
    />
  </template>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue"
import { type Field, formatValue } from "@auger/core/fields"
import { type FieldMode, type FieldSize, textSizeClass } from "@auger/ui/fields/useField"
import MarkdownEditModal from "@/components/editors/MarkdownEditModal.vue"
import MarkdownEditor from "@/components/editors/MarkdownEditor.vue"

const props = withDefaults(
  defineProps<{
    modelValue: unknown
    field: Field
    mode: FieldMode
    size?: FieldSize
  }>(),
  { size: "small" }
)

const emit = defineEmits<{
  "update:modelValue": [value: unknown]
  commit: [payload?: { advance?: boolean }]
  cancel: []
}>()

const modal = ref<InstanceType<typeof MarkdownEditModal> | null>(null)

const sizeClass = computed(() => textSizeClass(props.size))
const editable = computed(() => !!props.field.isEditable)

// What the surfaces show — the value formatted to a string the editor can render.
const displayValue = computed<string>(() => {
  const formatted = formatValue(props.field, props.modelValue)
  return formatted == null ? "" : String(formatted)
})

// The modal saves the whole value at once; emit the new value and commit
// (interaction persists).
function handleSave(value: string) {
  emit("update:modelValue", value)
  emit("commit")
}

// Editing opens the shared markdown modal — there's no inline editing surface.
watch(
  () => props.mode === "editing",
  (on) => {
    if (on) modal.value?.open()
  }
)
</script>

<style>
.markdown-field {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  overflow: hidden;
  cursor: default;

  /* The card host, which wraps by default, still wants the note on one line.
     In the grid, GridCell's `.cell-value` rules own this (and the tall-row
     wrap that goes with it). */
  > span {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
    width: 100%;
  }
}

.grid-editor .markdown-field-preview {
  position: relative;
  width: 100%;
  max-height: 60vh;
  overflow: auto;

  .expand-record {
    position: absolute;
    top: 0.25rem;
    right: 0.25rem;
    height: 20px;
    min-width: 22px;
    padding: 0;
    border: 1px solid var(--auger-border-light);
  }

  .markdown-editor .ProseMirror {
    padding: 0;
    min-height: auto;
    border: none;
    background: transparent;
  }
}
</style>
